import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaLock, FaSyncAlt, FaUserShield } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';
import ThreadList from '../components/messages/ThreadList';
import ThreadView from '../components/messages/ThreadView';
import * as api from '../lib/api';
import { buttonClass, mutedClass, tabClass } from '../components/uiStyles';

const STAT_KEYS = ['unread_threads', 'threads', 'messages', 'users'];

/** The admin inbox: every thread from every user, with replies. */
const AdminPage = () => {
    const { isDarkMode } = useTheme();
    const { t, i18n } = useTranslation();
    const { user, isAdmin, isLoading } = useAuth();

    const [threads, setThreads] = useState([]);
    const [stats, setStats] = useState(null);
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [status, setStatus] = useState('loading');

    const load = useCallback(async () => {
        try {
            const [threadData, statData] = await Promise.all([
                api.fetchAdminThreads(unreadOnly),
                api.fetchAdminStats(),
            ]);
            setThreads(threadData.threads);
            setStats(statData);
            setStatus('ready');
        } catch {
            setStatus('error');
        }
    }, [unreadOnly]);

    useEffect(() => {
        if (isAdmin) load();
    }, [isAdmin, load]);

    if (isLoading) {
        return (
            <div className="min-h-screen py-8">
                <div className="section-container">
                    <p className={mutedClass(isDarkMode)}>{t('messages.loading')}</p>
                </div>
            </div>
        );
    }

    // Signed out, or signed in with an account that is not on the admin list.
    if (!user || !isAdmin) {
        return (
            <div className="min-h-screen py-8">
                <div className="section-container max-w-xl">
                    <div className={`${isDarkMode ? 'bg-dark-overlay' : 'bg-white'} backdrop-blur-md rounded-2xl p-8 shadow-xl text-center`}>
                        <FaLock className={`mx-auto text-4xl mb-4 ${isDarkMode ? 'text-primary' : 'text-green-600'}`} />
                        <h1 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-light' : 'text-gray-800'}`}>
                            {t('admin.title')}
                        </h1>
                        <p className={`mb-6 ${mutedClass(isDarkMode)}`}>
                            {user ? t('admin.forbidden') : t('admin.signInRequired')}
                        </p>
                        {user ? (
                            <Link to="/" className={buttonClass(isDarkMode, 'primary')}>{t('useful.back')}</Link>
                        ) : (
                            <div className="flex justify-center"><GoogleSignInButton /></div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8">
            <div className="section-container">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mb-6"
                >
                    <Link
                        to="/"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${isDarkMode
                            ? 'bg-dark/60 text-primary hover:bg-dark border border-primary/20'
                            : 'bg-white text-green-600 hover:bg-gray-50 border border-gray-200'
                            } transition-all duration-300 shadow-md`}
                    >
                        <FaArrowLeft className="rtl:rotate-180" /> {t('useful.back')}
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`${isDarkMode ? 'bg-dark-overlay' : 'bg-white'} backdrop-blur-md rounded-2xl p-6 md:p-8 shadow-xl`}
                >
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <div className="flex items-center gap-3">
                            <FaUserShield className={`text-3xl ${isDarkMode ? 'text-primary' : 'text-green-600'}`} />
                            <div>
                                <h1 className={`text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-primary' : 'text-green-600'}`}>
                                    {t('admin.title')}
                                </h1>
                                <p className={`text-sm ${mutedClass(isDarkMode)}`}>{t('admin.subtitle')}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={load}
                            className={`${buttonClass(isDarkMode)} inline-flex items-center gap-2`}
                        >
                            <FaSyncAlt /> {t('admin.refresh')}
                        </button>
                    </div>

                    {stats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                            {STAT_KEYS.map((key) => (
                                <div
                                    key={key}
                                    className={`rounded-xl p-4 text-center ${isDarkMode ? 'bg-dark/40 border border-primary/10' : 'bg-gray-50 border border-gray-100'}`}
                                >
                                    <p className={`text-2xl font-bold ${isDarkMode ? 'text-primary' : 'text-green-600'}`}>
                                        {Number(stats[key] || 0).toLocaleString(i18n.language)}
                                    </p>
                                    <p className={`text-xs ${mutedClass(isDarkMode)}`}>{t(`admin.stats.${key}`)}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                        <button type="button" onClick={() => setUnreadOnly(false)} className={tabClass(isDarkMode, !unreadOnly)}>
                            {t('admin.allThreads')}
                        </button>
                        <button type="button" onClick={() => setUnreadOnly(true)} className={tabClass(isDarkMode, unreadOnly)}>
                            {t('admin.unreadOnly')}
                        </button>
                    </div>

                    {status === 'error' && (
                        <p className="text-sm text-red-500 mb-4">{t('messages.errors.network')}</p>
                    )}

                    <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
                        <div className="max-h-[60vh] overflow-y-auto pr-1">
                            <ThreadList
                                threads={threads}
                                activeId={activeId}
                                onSelect={setActiveId}
                                unreadKey="adminUnread"
                                showUser
                            />
                        </div>

                        <div className={`rounded-xl border min-h-[420px] ${isDarkMode ? 'border-primary/10 bg-dark/30' : 'border-gray-200 bg-gray-50'}`}>
                            {activeId ? (
                                <ThreadView key={activeId} threadId={activeId} role="admin" onChanged={load} />
                            ) : (
                                <p className={`p-6 text-sm ${mutedClass(isDarkMode)}`}>{t('admin.pickThread')}</p>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default AdminPage;
